import React from "react";
import { Link, useRouter } from "../router";
import { useAuth } from "../auth/AuthContext";
import { Icon, IconName } from "../icons";

type NavItem = { to: string; label: string; permiso?: string; disponible: boolean; icon: IconName };

const NAV_ITEMS: NavItem[] = [
  { to: "/", label: "Dashboard", disponible: true, icon: "home" },
  { to: "/empresa", label: "Configuración", permiso: "core.empresas:ver", disponible: true, icon: "settings" },
  { to: "/usuarios", label: "Usuarios", permiso: "core.usuarios:ver", disponible: true, icon: "user" },
  { to: "/roles", label: "Roles y permisos", permiso: "core.roles:ver", disponible: true, icon: "shield" },
  { to: "/parametros", label: "Parámetros", permiso: "core.parametros:ver", disponible: true, icon: "sliders" },
  { to: "/catalogos", label: "Catálogos", permiso: "core.catalogos:ver", disponible: true, icon: "tag" },
  { to: "/productos", label: "Productos", permiso: "core.catalogos:ver", disponible: true, icon: "barcode" },
  { to: "/compras", label: "Compras y Recepción", permiso: "compras:ver", disponible: true, icon: "cart" },
  { to: "/produccion", label: "Producción", permiso: "produccion:ver", disponible: true, icon: "factory" },
  { to: "/costeo", label: "Costeo", permiso: "costeo:ver", disponible: true, icon: "calculator" },
  { to: "/inventarios-op", label: "Inventarios", permiso: "inventarios:ver", disponible: true, icon: "box" },
  { to: "/comercial", label: "Comercial", permiso: "comercial:ver", disponible: true, icon: "briefcase" },
  { to: "/gerencial", label: "Gerencial", permiso: "gerencial:ver", disponible: true, icon: "barChart" },
  { to: "/contabilidad", label: "Contabilidad", permiso: "contabilidad:ver", disponible: true, icon: "book" },
  { to: "/nomina", label: "Nómina", permiso: "nomina:ver", disponible: true, icon: "users" },
  { to: "/cumplimiento", label: "Cumplimiento DIAN", permiso: "cumplimiento:ver", disponible: true, icon: "fileCheck" },
  { to: "/pos", label: "POS (mostrador)", permiso: "pos:ver", disponible: true, icon: "creditCard" },
  { to: "/notas", label: "Notas crédito/débito", permiso: "notas:ver", disponible: true, icon: "fileText" },
  { to: "/tesoreria", label: "Tesorería", permiso: "tesoreria:ver", disponible: true, icon: "landmark" },
  { to: "/activos", label: "Activos Fijos", permiso: "activos:ver", disponible: true, icon: "archive" },
  { to: "/restaurante", label: "Restaurante", permiso: "restaurante:ver", disponible: true, icon: "utensils" },
  { to: "/dian", label: "Facturación DIAN", permiso: "dian:ver", disponible: true, icon: "shield" },
  { to: "/informes", label: "Informes Ejecutivos", permiso: "informes:ver", disponible: true, icon: "lineChart" },
  { to: "/reportes", label: "Reportes", disponible: true, icon: "printer" },
  { to: "/auditoria", label: "Auditoría (bitácora)", permiso: "core.auditoria:ver", disponible: true, icon: "search" },
];

// SIGEPRO-EP se muestra por separado del núcleo privado: es un paquete de
// módulos distinto para entidades públicas, con su propio catálogo de
// cuentas (CGC) — nunca aparece mezclado con la lista de arriba.
const NAV_ITEM_EP: NavItem = {
  to: "/sigepro-ep", label: "SIGEPRO-EP · Entidades Públicas", permiso: "sigepro_ep:ver", disponible: true, icon: "building",
};

function iniciales(nombre?: string) {
  if (!nombre) return "?";
  const partes = nombre.trim().split(/\s+/);
  return ((partes[0]?.[0] || "") + (partes[1]?.[0] || "")).toUpperCase() || "?";
}

export function Shell({ children, titulo, breadcrumb }: { children: React.ReactNode; titulo: string; breadcrumb: string }) {
  const { path } = useRouter();
  const { sesion, logout, tienePermiso, moduloDisponibleParaRuta } = useAuth();

  return (
    <div className="min-h-screen flex bg-surface">
      <aside className="w-64 shrink-0 bg-gradient-to-b from-navy to-navy-light/95 text-white flex flex-col">
        <div className="px-5 pt-6 pb-5 flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-xl bg-accent flex items-center justify-center font-display font-extrabold text-sm shrink-0">
            SG
          </div>
          <div className="min-w-0">
            <div className="font-display text-base font-bold tracking-tight leading-tight">SIGEPRO ERP</div>
            <div className="text-[11px] text-slate-300/90 truncate">{sesion?.empresa?.nombre || "Planta de Transformación"}</div>
          </div>
        </div>
        <nav className="flex-1 px-2 space-y-0.5 overflow-y-auto pb-4">
          {NAV_ITEMS.map((item) => {
            const activo = path.split("?")[0] === item.to;
            // Licenciamiento por módulos (agosto 2026): además del permiso
            // RBAC, un ítem del menú también se oculta si la empresa no
            // tiene contratado (o le venció) el módulo que lo respalda — ver
            // modulosCatalogo.RUTA_MODULOS. El backend es la barrera real;
            // esto es solo para no mostrar botones que de todos modos
            // fallarían con 403.
            const puedeVer = (!item.permiso || tienePermiso(item.permiso)) && moduloDisponibleParaRuta(item.to);
            if (!item.disponible) {
              return (
                <div
                  key={item.to}
                  className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-slate-500 cursor-not-allowed select-none"
                  title="Próximamente disponible"
                >
                  <Icon name={item.icon} className="shrink-0 opacity-60" />
                  <span className="flex-1 truncate">{item.label}</span>
                </div>
              );
            }
            if (!puedeVer) return null;
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`group relative flex items-center gap-2.5 rounded-lg pl-3 pr-2.5 py-2 text-sm font-medium transition-colors ${
                  activo ? "bg-white/12 text-white" : "text-slate-300 hover:bg-white/8 hover:text-white"
                }`}
              >
                {activo && <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-accent" />}
                <Icon name={item.icon} className={activo ? "text-accent shrink-0" : "text-slate-400 shrink-0 group-hover:text-slate-200"} />
                <span className="flex-1 truncate">{item.label}</span>
              </Link>
            );
          })}
          {(!NAV_ITEM_EP.permiso || tienePermiso(NAV_ITEM_EP.permiso)) && moduloDisponibleParaRuta(NAV_ITEM_EP.to) && (
            <>
              <div className="my-2 border-t border-white/10" />
              <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Sector público</div>
              <Link
                to={NAV_ITEM_EP.to}
                className={`group relative flex items-center gap-2.5 rounded-lg pl-3 pr-2.5 py-2 text-sm font-medium transition-colors ${
                  path.split("?")[0] === NAV_ITEM_EP.to ? "bg-amber-600/90 text-white" : "text-slate-300 hover:bg-white/8 hover:text-white"
                }`}
              >
                <Icon name={NAV_ITEM_EP.icon} className={path.split("?")[0] === NAV_ITEM_EP.to ? "text-white shrink-0" : "text-amber-500/80 shrink-0 group-hover:text-amber-400"} />
                <span className="flex-1 truncate">{NAV_ITEM_EP.label}</span>
              </Link>
            </>
          )}
        </nav>
        <div className="p-3 border-t border-white/10">
          <button onClick={logout} className="w-full flex items-center gap-2.5 rounded-lg text-left text-sm text-slate-300 hover:text-white hover:bg-white/8 px-3 py-2 transition-colors">
            <Icon name="logout" className="shrink-0" />
            Cerrar sesión
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-[70px] shrink-0 bg-white/80 backdrop-blur border-b border-borderc flex items-center justify-between px-6">
          <div>
            <div className="text-[11px] text-textmuted">{breadcrumb}</div>
            <h1 className="font-display text-lg font-bold text-slate-800">{titulo}</h1>
          </div>
          <div className="flex items-center gap-2.5 rounded-full border border-borderc bg-surface pl-1.5 pr-3.5 py-1.5 text-sm text-slate-700">
            <span className="h-7 w-7 rounded-full bg-accent text-white text-[11px] font-bold flex items-center justify-center shrink-0">
              {iniciales(sesion?.usuario.nombre)}
            </span>
            <span className="truncate max-w-[220px]">
              {sesion?.usuario.nombre} · {sesion?.roles.map((r) => r.nombre).join(", ")}
            </span>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-6">{children}</main>
      </div>
    </div>
  );
}
